import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { warmupBackend } from '../services/api';

// Lazy-load heavy 3D both components
const PillScene3D = lazy(() => import('./PillScene3D'));

// ── Design tokens (Neo-Brutalist palette) ─────────────────────────────────────
const T = {
  bg: '#fdfbf7',
  surface: '#ffffff',
  card: '#ffffff',
  border: '#2d3436',
  white: '#ffffff',
  teal: '#0984e3',
  tealDim: '#2e86de',
  danger: '#ff6b6b',
  amber: '#ff9f43',
  blue: '#0984e3',
  gray: '#636e72',
  text: '#2d3436',
};

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const MODULES = [
  { name: 'STOPP/START', desc: 'EU evidence-based prescribing criteria for older adults', icon: '⚕' },
  { name: 'Beers Criteria', desc: 'AGS potentially inappropriate medication list', icon: '📋' },
  { name: 'ACB Score', desc: 'Anticholinergic cognitive burden calculation', icon: '🧠' },
  { name: 'Drug Interactions', desc: 'Multi-drug interaction risk stratification', icon: '⚡' },
  { name: 'Renal Dosing', desc: 'eGFR-adjusted dosing recommendations', icon: '🫁' },
  { name: 'Falls Risk', desc: 'Medication-related falls risk assessment', icon: '⚠' },
  { name: 'QTc Prolongation', desc: 'Cardiac arrhythmia risk screening', icon: '❤' },
  { name: 'STARSGeri', desc: 'Geriatric-specific prescribing guidance', icon: '⭐' },
  { name: 'Deprescribing', desc: 'Safe tapering and discontinuation protocols', icon: '✅' },
];

// ── Neo-Brutalist card ─────────────────────────────────────────────────────────
function NeoCard({ children, className = '', onClick, accent = T.teal, animate = true }) {
  const [hov, setHov] = useState(false);
  return (
    <motion.div
      className={`relative ${className}`}
      style={{
        background: T.card,
        border: `3px solid ${T.border}`,
        boxShadow: hov ? `2px 2px 0 ${accent}` : `6px 6px 0 ${accent}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        transform: hov ? 'translate(4px,4px)' : 'translate(0,0)',
        borderRadius: 4,
      }}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onClick={onClick}
      initial={animate ? { opacity: 0, y: 24 } : {}}
      whileInView={animate ? { opacity: 1, y: 0 } : {}}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

// ── Tag / badge ───────────────────────────────────────────────────────────────
function Tag({ children, color = T.teal }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: '#ffffff',
        background: color,
        border: `2px solid ${T.border}`,
        borderRadius: 3,
        boxShadow: `2px 2px 0 ${T.border}`,
        fontFamily: 'Space Mono, monospace',
      }}
    >
      {children}
    </span>
  );
}

class CanvasErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

// ── Animated counter ──────────────────────────────────────────────────────────
function AnimCounter({ to, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  const started = useRef(false);

  const start = () => {
    if (started.current) return;
    started.current = true;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      setVal(Math.round(p * to));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) start(); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
    // eslint-disable-next-line
  }, []);

  return <span ref={ref}>{val}{suffix}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [pillClicked, setPillClicked] = useState(false);
  const [backendState, setBackendState] = useState('idle'); // idle | starting | ready | error
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef(null);
  const sceneRef = useRef(null);
  const [sceneVisible, setSceneVisible] = useState(true);
  const revealRef = useRef(0);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 600], [0, -80]);
  const scaleHero = useTransform(scrollY, [0, 400], [1, 0.96]);

  // Google Font inject
  useEffect(() => {
    const existing = document.querySelector('link[data-deprescribex-fonts="1"]');
    if (existing) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800;900&family=Space+Mono:wght@400;700&display=swap';
    link.setAttribute('data-deprescribex-fonts', '1');
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => setSceneVisible(e.isIntersecting),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useMotionValueEvent(scrollY, 'change', (y) => {
    revealRef.current = clamp01((y - 180) / 900);
  });

  const handleStartBackend = async () => {
    if (backendState === 'starting' || backendState === 'ready') return;
    setBackendState('starting');
    try {
      await warmupBackend();
      setBackendState('ready');
      navigate('/app');
    } catch (e) {
      setBackendState('error');
    }
  };

  const mobileNavItems = [
    { label: 'Modules', href: '#modules' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Evidence', href: '#evidence' },
  ];

  return (
    <div style={{ background: T.bg, color: T.text, minHeight: '100vh', fontFamily: 'Space Grotesk, sans-serif', overflowX: 'hidden' }}>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 68,
        background: 'rgba(253,251,247,0.94)',
        backdropFilter: 'blur(12px)',
        borderBottom: `2px solid ${T.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, background: T.teal, borderRadius: 6,
            border: `3px solid ${T.border}`, boxShadow: `4px 4px 0 ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 900, color: '#ffffff',
          }}>Rx</div>
          <span style={{ fontWeight: 900, fontSize: 20, letterSpacing: '-0.04em' }}>
            Deprescri<span style={{ color: T.teal }}>Bex</span>
          </span>
        </div>

        <div className="hidden md:flex items-center gap-2">
          {['MODULES', 'HOW IT WORKS', 'EVIDENCE'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase().replace(/ /g, '-')}`}
              style={{
                padding: '6px 14px',
                fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                color: T.gray, textDecoration: 'none',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              {item}
            </a>
          ))}
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleStartBackend}
            disabled={backendState === 'starting'}
            style={{
              padding: '10px 18px',
              background: backendState === 'ready' ? '#00b894' : (backendState === 'error' ? '#ff6b6b' : T.border),
              color: '#ffffff',
              border: `3px solid ${T.border}`,
              borderRadius: 5,
              boxShadow: `4px 4px 0 ${T.border}`,
              fontWeight: 900,
              fontSize: 13,
              letterSpacing: '0.08em',
              cursor: backendState === 'starting' ? 'not-allowed' : 'pointer',
              opacity: backendState === 'starting' ? 0.75 : 1,
              fontFamily: 'Space Grotesk, sans-serif',
            }}
          >
            {backendState === 'ready'
              ? 'BACKEND READY'
              : backendState === 'starting'
                ? 'STARTING BACKEND…'
                : backendState === 'error'
                  ? 'RETRY BACKEND'
                  : 'START BACKEND'}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/app')}
            style={{
              padding: '10px 22px', background: T.teal, color: '#ffffff',
              border: `3px solid ${T.border}`, borderRadius: 5,
              boxShadow: `4px 4px 0 ${T.border}`, fontWeight: 900,
              fontSize: 13, letterSpacing: '0.08em', cursor: 'pointer',
              fontFamily: 'Space Grotesk, sans-serif',
            }}
          >
            LAUNCH APP →
          </motion.button>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            style={{
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: T.card,
              border: `3px solid ${T.border}`,
              borderRadius: 6,
              boxShadow: `4px 4px 0 ${T.border}`,
              cursor: 'pointer',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              {mobileMenuOpen ? (
                <>
                  <path d="M6 6l12 12" stroke={T.border} strokeWidth="3" strokeLinecap="round" />
                  <path d="M18 6L6 18" stroke={T.border} strokeWidth="3" strokeLinecap="round" />
                </>
              ) : (
                <>
                  <path d="M4 6h16" stroke={T.border} strokeWidth="3" strokeLinecap="round" />
                  <path d="M4 12h16" stroke={T.border} strokeWidth="3" strokeLinecap="round" />
                  <path d="M4 18h16" stroke={T.border} strokeWidth="3" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          className="md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.28)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              top: 76,
              left: 16,
              right: 16,
              background: T.card,
              border: `3px solid ${T.border}`,
              boxShadow: `6px 6px 0 ${T.border}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'grid', gap: 8 }}>
                {mobileNavItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 6,
                      border: `2px solid ${T.border}`,
                      textDecoration: 'none',
                      color: T.border,
                      fontWeight: 900,
                      letterSpacing: '0.06em',
                      fontSize: 12,
                      textTransform: 'uppercase',
                      fontFamily: 'Space Mono, monospace',
                      background: T.bg,
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleStartBackend();
                  }}
                  disabled={backendState === 'starting'}
                  style={{
                    padding: '12px 14px',
                    background: backendState === 'ready' ? '#00b894' : (backendState === 'error' ? '#ff6b6b' : T.border),
                    color: '#ffffff',
                    border: `3px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: `4px 4px 0 ${T.border}`,
                    fontWeight: 900,
                    fontSize: 13,
                    letterSpacing: '0.08em',
                    cursor: backendState === 'starting' ? 'not-allowed' : 'pointer',
                    opacity: backendState === 'starting' ? 0.75 : 1,
                    fontFamily: 'Space Grotesk, sans-serif',
                    textTransform: 'uppercase',
                  }}
                >
                  {backendState === 'ready'
                    ? 'Backend Ready'
                    : backendState === 'starting'
                      ? 'Starting Backend...'
                      : backendState === 'error'
                        ? 'Retry Backend'
                        : 'Start Backend'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/app');
                  }}
                  style={{
                    padding: '12px 14px',
                    background: T.teal,
                    color: '#ffffff',
                    border: `3px solid ${T.border}`,
                    borderRadius: 8,
                    boxShadow: `4px 4px 0 ${T.border}`,
                    fontWeight: 900,
                    fontSize: 13,
                    letterSpacing: '0.08em',
                    cursor: 'pointer',
                    fontFamily: 'Space Grotesk, sans-serif',
                    textTransform: 'uppercase',
                  }}
                >
                  Launch App
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          paddingTop: 68, position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Grid background */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `linear-gradient(${T.border} 1px, transparent 1px), linear-gradient(90deg, ${T.border} 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
          opacity: 0.08,
        }} />

        {/* Teal radial glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 800, height: 800,
          background: 'radial-gradient(circle, rgba(9,132,227,0.14) 0%, transparent 72%)',
          pointerEvents: 'none',
        }} />

        <motion.div style={{ y: parallaxY, scale: scaleHero, width: '100%', maxWidth: 1200, padding: '0 24px' }}>

          {/* Status tag */}
          

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            style={{
              textAlign: 'center',
              fontSize: 'clamp(40px, 7vw, 88px)',
              fontWeight: 900, lineHeight: 1.0,
              letterSpacing: '-0.04em',
              marginBottom: 12,
            }}
          >
            AI THAT REMOVES
            <br />
            <span style={{ color: T.teal, position: 'relative', display: 'inline-block' }}>
              DANGEROUS
              <svg style={{ position: 'absolute', bottom: -4, left: 0, width: '100%', height: 8 }} viewBox="0 0 300 8" preserveAspectRatio="none">
                <line x1="0" y1="6" x2="300" y2="6" stroke={T.teal} strokeWidth="3" strokeDasharray="6 4" />
              </svg>
            </span>{' '}
            MEDICINES
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            style={{
              textAlign: 'center', color: T.gray, fontSize: 19,
              maxWidth: 600, margin: '0 auto 52px',
              fontWeight: 500, lineHeight: 1.55,
            }}
          >
            Clinical-grade AI that analyzes patient medications through 9 evidence-based modules
            to identify and safely reduce polypharmacy.
          </motion.p>

          
            

        

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 40, flexWrap: 'wrap' }}
          >
            <motion.button
              whileHover={{ x: 3, y: 3, boxShadow: `2px 2px 0 ${T.teal}` }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/app')}
              style={{
                padding: '16px 40px', background: T.white, color: T.text,
                border: `3px solid ${T.border}`, borderRadius: 6,
                boxShadow: `7px 7px 0 ${T.teal}`, fontWeight: 900,
                fontSize: 16, letterSpacing: '0.06em', cursor: 'pointer',
                fontFamily: 'Space Grotesk, sans-serif',
              }}
            >
              START ANALYSIS →
            </motion.button>
            <motion.button
              whileHover={{ x: 3, y: 3, boxShadow: `2px 2px 0 ${T.border}` }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                padding: '16px 40px', background: 'transparent', color: T.text,
                border: `3px solid ${T.border}`, borderRadius: 6,
                boxShadow: `7px 7px 0 ${T.border}`, fontWeight: 800,
                fontSize: 16, letterSpacing: '0.06em', cursor: 'pointer',
                fontFamily: 'Space Grotesk, sans-serif',
              }}
            >
              HOW IT WORKS
            </motion.button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 36, flexWrap: 'wrap' }}
          >
            {['STOPP/START', 'BEERS CRITERIA', 'HIPAA COMPLIANT', 'EVIDENCE-BASED'].map((b) => (
              <span key={b} style={{ color: T.gray, fontSize: 11, fontFamily: 'Space Mono, monospace', fontWeight: 700, letterSpacing: '0.1em' }}>
                ✓ {b}
              </span>
            ))}
          </motion.div>

        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6 }}
          style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', color: T.tealDim, fontSize: 22, cursor: 'pointer' }}
          onClick={() => document.getElementById('stats')?.scrollIntoView({ behavior: 'smooth' })}
        >
          ↓
        </motion.div>
      </section>

      {/* ── 3D PILL SCENE (full-width background strip) ────────────────────── */}
      <section ref={sceneRef} style={{
        height: 400, position: 'relative', background: `linear-gradient(to bottom, ${T.bg}, #e8f4ff, ${T.bg})`,
        border: `2px solid ${T.border}`,
        borderLeft: 'none', borderRight: 'none',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 2, textAlign: 'center' }}>
          <Tag color={T.amber}>AI SCAN BEAM LIVE — HOVER PILLS TO ANALYZE</Tag>
          <p style={{ fontSize: 12, color: T.gray, fontFamily: 'Space Mono, monospace', marginTop: 8, letterSpacing: '0.08em' }}>
            🟢 SAFE &nbsp;&nbsp; 🔴 FLAGGED INTERACTION
          </p>
        </div>
        <CanvasErrorBoundary
          fallback={<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gray }}>3D UNAVAILABLE</div>}
        >
          <Suspense fallback={<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gray }}>Loading 3D Scene...</div>}>
            {sceneVisible ? <PillScene3D revealRef={revealRef} isRevealed={pillClicked} onClickPill={() => setPillClicked((v) => !v)} /> : null}
          </Suspense>
        </CanvasErrorBoundary>
      </section>

      {/* ── STATS BAR ────────────────────────────────────────────────────────── */}
      <section id="stats" style={{ background: T.surface, borderTop: `3px solid ${T.border}`, borderBottom: `3px solid ${T.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '52px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
          {[
            { n: 9, suf: '', label: 'CLINICAL MODULES', desc: 'Evidence-based frameworks', color: T.teal },
            { n: 40, suf: '%', label: 'POLYPHARMACY RATE', desc: 'In adults 65+', color: T.amber },
            { n: 60, suf: 's', label: 'ANALYSIS TIME', desc: 'Faster than chart review', color: T.blue },
            { n: 1200, suf: '+', label: 'DRUG INTERACTIONS', desc: 'Checked per patient', color: T.danger },
          ].map((s, i) => (
            <NeoCard key={i} accent={s.color}>
              <div style={{ padding: '28px 24px' }}>
                <div style={{ fontSize: 52, fontWeight: 900, color: s.color, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: 'Space Grotesk, sans-serif' }}>
                  <AnimCounter to={s.n} suffix={s.suf} />
                </div>
                <div style={{ fontSize: 12, fontFamily: 'Space Mono, monospace', fontWeight: 700, letterSpacing: '0.1em', color: T.text, marginTop: 10 }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 13, color: T.gray, marginTop: 4 }}>{s.desc}</div>
              </div>
            </NeoCard>
          ))}
        </div>
      </section>

      {/* ── PROBLEM / SOLUTION ───────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '88px 24px' }}>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 56 }}>
          <Tag color={T.danger}>THE PROBLEM</Tag>
          <h2 style={{ fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 900, marginTop: 16, letterSpacing: '-0.04em' }}>
            Polypharmacy Is a<br />
            <span style={{ color: T.danger }}>Silent Crisis</span>
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          <NeoCard accent={T.danger}>
            <div style={{ padding: '32px 28px' }}>
              <div style={{ fontSize: 11, fontFamily: 'Space Mono, monospace', fontWeight: 700, color: T.danger, letterSpacing: '0.12em', marginBottom: 20 }}>WITHOUT DEPRESCRIBEX</div>
              {['Manual chart review takes hours', 'Missed drug-drug interactions', 'No systematic criteria applied', 'Cognitive bias in prescribing', 'Adverse events go undetected'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <span style={{ color: T.danger, fontSize: 18, marginTop: -2 }}>✕</span>
                  <span style={{ color: T.gray, fontSize: 15, lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>
          </NeoCard>

          <NeoCard accent={T.teal}>
            <div style={{ padding: '32px 28px' }}>
              <div style={{ fontSize: 11, fontFamily: 'Space Mono, monospace', fontWeight: 700, color: T.teal, letterSpacing: '0.12em', marginBottom: 20 }}>WITH DEPRESCRIBEX</div>
              {['9-module AI analysis in seconds', 'All interactions cross-referenced', 'STOPP/START + Beers applied automatically', 'Objective evidence-based score', 'Prioritized deprescribing recommendations'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                  <span style={{ color: T.teal, fontSize: 18, marginTop: -2 }}>✓</span>
                  <span style={{ color: T.text, fontSize: 15, lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>
          </NeoCard>
        </div>
      </section>

      {/* ── MODULES GRID ──────────────────────────────────────────────────────── */}
      <section id="modules" style={{ background: T.surface, borderTop: `3px solid ${T.border}`, borderBottom: `3px solid ${T.border}`, padding: '88px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 52 }}>
            <Tag color={T.teal}>9 MODULES</Tag>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 900, marginTop: 16, letterSpacing: '-0.04em' }}>
              Every Framework.<br />
              <span style={{ color: T.teal }}>One Platform.</span>
            </h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {MODULES.map((mod, i) => (
              <NeoCard key={i} accent={i % 3 === 0 ? T.teal : i % 3 === 1 ? T.blue : T.amber}>
                <div style={{ padding: '24px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                    <div style={{
                      width: 42, height: 42, background: T.card,
                      border: `2px solid ${T.border}`, borderRadius: 4,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22,
                    }}>{mod.icon}</div>
                    <h3 style={{ fontSize: 15, fontWeight: 900, letterSpacing: '-0.01em', color: T.text }}>{mod.name}</h3>
                  </div>
                  <p style={{ fontSize: 13, color: T.gray, lineHeight: 1.55 }}>{mod.desc}</p>
                </div>
              </NeoCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────────── */}
      <section id="how-it-works" style={{ maxWidth: 1100, margin: '0 auto', padding: '88px 24px' }}>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} style={{ textAlign: 'center', marginBottom: 56 }}>
          <Tag color={T.blue}>PROCESS</Tag>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 900, marginTop: 16, letterSpacing: '-0.04em' }}>
            Three Steps to<br /><span style={{ color: T.blue }}>Clinical Clarity</span>
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {[
            { n: '01', title: 'INPUT PATIENT DATA', desc: 'Enter medications, diagnoses, lab values, and patient demographics into the structured form.', color: T.teal },
            { n: '02', title: 'AI MULTI-MODULE SCAN', desc: '9 evidence-based frameworks analyze every medication simultaneously for interactions and risks.', color: T.blue },
            { n: '03', title: 'RECEIVE RECOMMENDATIONS', desc: 'Get prioritized, evidence-graded deprescribing suggestions with tapering protocols.', color: T.amber },
          ].map((s, i) => (
            <NeoCard key={i} accent={s.color}>
              <div style={{ padding: '32px 28px' }}>
                <div style={{ fontSize: 56, fontWeight: 900, color: s.color, lineHeight: 1, letterSpacing: '-0.04em', marginBottom: 16, fontFamily: 'Space Mono, monospace', opacity: 0.7 }}>{s.n}</div>
                <h3 style={{ fontSize: 16, fontWeight: 900, letterSpacing: '0.04em', color: T.text, marginBottom: 12 }}>{s.title}</h3>
                <p style={{ color: T.gray, fontSize: 14, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            </NeoCard>
          ))}
        </div>
      </section>

      {/* ── EVIDENCE SECTION ─────────────────────────────────────────────────── */}
      <section id="evidence" style={{ background: T.surface, borderTop: `3px solid ${T.border}`, borderBottom: `3px solid ${T.border}`, padding: '72px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <Tag color={T.amber}>EVIDENCE-BASED</Tag>
            <h2 style={{ fontSize: 'clamp(26px, 4vw, 48px)', fontWeight: 900, marginTop: 16, marginBottom: 12, letterSpacing: '-0.04em' }}>
              Built on<br /><span style={{ color: T.amber }}>Peer-Reviewed Science</span>
            </h2>
            <p style={{ color: T.gray, fontSize: 16, lineHeight: 1.6, maxWidth: 580, margin: '0 auto 48px' }}>
              Every recommendation maps to published clinical criteria used in hospital formularies worldwide.
            </p>
          </motion.div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            {['STOPP/START Criteria', 'Beers Criteria', 'DDInter','TimeToBenefit'].map((c) => (
              <motion.div
                key={c}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 12 }}
                viewport={{ once: true }}
                style={{
                  padding: '10px 18px',
                  border: `2px solid ${T.border}`,
                  borderRadius: 4, background: T.card,
                  boxShadow: `4px 4px 0 ${T.border}`,
                  fontSize: 13, fontWeight: 700, color: T.text,
                  fontFamily: 'Space Mono, monospace',
                }}
              >
                {c}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '88px 24px', textAlign: 'center' }}>
        <NeoCard accent={T.teal} animate={true}>
          <div style={{ padding: '64px 40px' }}>
            <Tag color={T.teal}>FREE TO USE</Tag>
            <h2 style={{ fontSize: 'clamp(32px, 5vw, 64px)', fontWeight: 900, marginTop: 20, marginBottom: 16, letterSpacing: '-0.04em' }}>
              Start Safer<br /><span style={{ color: T.teal }}>Prescribing Today</span>
            </h2>
            <p style={{ color: T.gray, fontSize: 17, maxWidth: 500, margin: '0 auto 40px', lineHeight: 1.6 }}>
              No signup. No cost. Just evidence-based AI analysis for better patient outcomes.
            </p>
            <motion.button
              whileHover={{ x: 4, y: 4, boxShadow: `2px 2px 0 ${T.border}` }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/app')}
              style={{
                padding: '20px 56px', background: T.teal, color: '#ffffff',
                border: `3px solid ${T.border}`, borderRadius: 6,
                boxShadow: `8px 8px 0 ${T.border}`, fontWeight: 900,
                fontSize: 18, letterSpacing: '0.06em', cursor: 'pointer',
                fontFamily: 'Space Grotesk, sans-serif',
              }}
            >
              ANALYZE PATIENT NOW →
            </motion.button>
          </div>
        </NeoCard>
      </section>

      {/* ── FOOTER ────────────────────────────────────────────────────────────── */}
      <footer style={{
        background: T.surface, borderTop: `3px solid ${T.border}`,
        padding: '36px 24px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, background: T.teal, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#ffffff', fontSize: 14 }}>Rx</div>
            <span style={{ fontWeight: 900, fontSize: 16 }}>DeprescriBex</span>
            <span style={{ color: T.border, fontSize: 20 }}>|</span>
            <span style={{ fontFamily: 'Space Mono, monospace', fontSize: 11, color: T.gray, letterSpacing: '0.08em' }}>v1.0 · 9-MODULE ANALYSIS</span>
          </div>
          <p style={{ color: T.gray, fontSize: 12, maxWidth: 600, margin: '0 auto', lineHeight: 1.7, fontFamily: 'Space Mono, monospace' }}>
            ⚠ FOR CLINICAL DECISION SUPPORT ONLY. NOT A SUBSTITUTE FOR PHYSICIAN JUDGMENT.<br />
            ALWAYS CONSULT A LICENSED HEALTHCARE PROFESSIONAL BEFORE MODIFYING MEDICATIONS.
          </p>
        </div>
      </footer>

      {/* Inline nav-link-desktop CSS */}
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 12px; background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border: 2px solid #f1f5f9; border-radius: 6px; }
      `}</style>
    </div>
  );
}
